import { useMemo } from 'react';

import logger from '../utility/logger';
import {
    buildJwPlaybackConfig,
    JwPlaybackConfigFragment,
    readNetworkConditions,
    resolvePlaybackOptimization,
} from '../utility/playbackOptimization';
import { useAppSelector } from '.';

/**
 * Single source of truth for the on-demand playback experiment and its narrower HLS-only path.
 *
 * Two conditions gate everything: the `PlaybackOptimization:Enabled` key in Azure App
 * Configuration, and the source being on-demand. Live webcasts run through the Amazon IVS provider,
 * a different engine where none of these levers exist and where poking the media element is a
 * liability rather than a fix, so they are excluded outright. Within on-demand, only the actual HLS
 * path should be tagged as optimized: JW's hls.js config, Mux cohort tagging, the watchdog, and
 * poster-mount behaviour do not belong on the standard MP4 fallback.
 */
export const usePlaybackOptimization = () => {
    const playbackOptimization = useAppSelector(
        (state) => state.clientConfig?.playbackOptimization
    );
    const isWebcast = useAppSelector((state) => state.player.launchDetails.isWebcast);
    const videoSourceType = useAppSelector((state) => state.player.videoSourceType);

    const options = useMemo(
        () => resolvePlaybackOptimization(playbackOptimization),
        [playbackOptimization]
    );

    const isEnabledForOnDemand = options.enabled && !isWebcast;
    const isOptimized = isEnabledForOnDemand && videoSourceType === 'hls';

    const jwConfig = useMemo<JwPlaybackConfigFragment | null>(() => {
        if (!isOptimized) return null;

        const conditions = readNetworkConditions();
        const config = buildJwPlaybackConfig(options, conditions);

        logger.log('Optimized playback config applied', { config, conditions });

        return config;
    }, [isOptimized, options]);

    return {
        /** True when the flag is on for any on-demand source, before narrowing to the active media path. */
        isEnabledForOnDemand,
        /** True only when the flag is on and the current source is the on-demand HLS path. */
        isOptimized,
        options,
        jwConfig,
    };
};

export default usePlaybackOptimization;
